import React, { useState, useEffect } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Box,
  Divider,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import EmailIcon from '@mui/icons-material/Email';
import ResetPasswordDialog from './ResetPasswordDialog';
import UpdateEmailDialog from './UpdateEmailDialog';
import { useAuth } from '../../contexts/AuthContext';
import { getServices } from '../../services/serviceManagementService';
import { serviceTypeLabels, SERVICE_ROLES } from '../../constants/services';

const ServiceList = () => {
  const { currentUser, userSchool } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [updateEmailDialogOpen, setUpdateEmailDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      if (!currentUser || !userSchool) {
        console.log('ServiceList: Utilisateur ou école non défini');
        return;
      }

      try {
        console.log('ServiceList: Début de la récupération des services pour l\'école:', userSchool.id);
        setLoading(true);
        setError(null);
        
        // Utiliser la fonction getServices pour récupérer les services techniques
        const result = await getServices(userSchool.id);
        console.log('ServiceList: Services récupérés:', result);
        
        if (result && result.services) {
          setServices(result.services);
        } else {
          setServices([]);
        }
      } catch (err) {
        console.error('ServiceList: Erreur lors de la récupération des services:', err);
        setError('Erreur lors de la récupération des services: ' + (err.message || err));
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [currentUser, userSchool]);

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return (
      <Typography color="error">
        {error}
      </Typography>
    );
  }
  
  const handleOpenResetPasswordDialog = (service) => {
    setSelectedService(service);
    setResetPasswordDialogOpen(true);
  };

  const handleCloseResetPasswordDialog = () => {
    setResetPasswordDialogOpen(false);
    setSelectedService(null);
  };

  const handleOpenUpdateEmailDialog = (service) => {
    setSelectedService(service);
    setUpdateEmailDialogOpen(true);
  };

  const handleCloseUpdateEmailDialog = () => {
    setUpdateEmailDialogOpen(false);
    setSelectedService(null);
  };

  const handleServiceUpdate = (updatedService) => {
    // Mettre à jour le service dans la liste locale
    setServices(services.map(service => 
      service.id === updatedService.id ? updatedService : service
    ));
  };

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Liste des Services
      </Typography>
      {services.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          Aucun service trouvé
        </Typography>
      ) : (
        <List>
          {services.map((service) => (
            <React.Fragment key={service.id}>
              <ListItem>
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1">{service.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {service.type && (
                        <Chip 
                          label={serviceTypeLabels[service.type] || service.type} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ mr: 1 }}
                        />
                      )}
                      <Chip 
                        label={service.role === SERVICE_ROLES.TECHNICAL ? 'Technique' : 'Demandeur'} 
                        size="small" 
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                      {service.email && (
                        <>
                          <Tooltip title="Réinitialiser le mot de passe">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenResetPasswordDialog(service)}
                            >
                              <LockResetIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Modifier l'email">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenUpdateEmailDialog(service)}
                            >
                              <EmailIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {service.email}
                  </Typography>
                </Box>
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          ))}
        </List>
      )}
      
      {/* Dialogue de réinitialisation de mot de passe */}
      {selectedService && (
        <ResetPasswordDialog
          open={resetPasswordDialogOpen}
          onClose={handleCloseResetPasswordDialog}
          service={selectedService}
        />
      )}
      
      {/* Dialogue de modification d'email */}
      {selectedService && (
        <UpdateEmailDialog
          open={updateEmailDialogOpen}
          onClose={handleCloseUpdateEmailDialog}
          service={selectedService}
          onUpdate={handleServiceUpdate}
        />
      )}
    </Paper>
  );
};

export default ServiceList;
